import React, { useMemo } from "react";
import {
    Grid,
    Typography,
    Box,
    Skeleton,
    Fade,
    Divider,
    Button,
    FormControl,
    Select,
    MenuItem,
    TextField,
    useTheme,
} from "@mui/material";
import {
    LocalShipping,
    StickyNote2,
    ConfirmationNumber,
    CheckCircle,
    Inventory,
    Paid,
    DeliveryDining,
    SwapHoriz,
    TrendingUp,
    Refresh,
    CalendarMonth,
    Edit,
    Insights,
} from "@mui/icons-material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useLazyGetMyDaySummaryQuery } from "store/services/myDayApi";
import { useAppMeta } from "hooks";

const MAX_RANGE_DAYS = 7;

const METRICS = [
    { key: "shipmentCreated", label: "Shipment Created", icon: <LocalShipping />, color: "#1976d2", lightBg: "#eff6ff", darkBg: "#0f172a" },
    { key: "notesAdded", label: "Notes Added", icon: <StickyNote2 />, color: "#ed6c02", lightBg: "#fff7ed", darkBg: "#271a08" },
    { key: "ticketCreated", label: "Ticket Created", icon: <ConfirmationNumber />, color: "#9c27b0", lightBg: "#faf5ff", darkBg: "#1a0f2e" },
    { key: "ticketClosed", label: "Ticket Closed", icon: <CheckCircle />, color: "#16a34a", lightBg: "#f0fdf4", darkBg: "#0a1f12" },
    { key: "shipmentsDelivered", label: "Shipments Delivered", icon: <Inventory />, color: "#0288d1", lightBg: "#f0f9ff", darkBg: "#0a1f2e" },
    { key: "cocCollected", label: "COC Collected", icon: <Paid />, color: "#dc2626", lightBg: "#fef2f2", darkBg: "#2a0a0a" },
    { key: "deliveryCollected", label: "Delivery Collected", icon: <DeliveryDining />, color: "#16a34a", lightBg: "#f0fdf4", darkBg: "#0a1f12" },
    { key: "codTransferred", label: "COD Transferred", icon: <SwapHoriz />, color: "#f97316", lightBg: "#fff7ed", darkBg: "#271a08" },
];

const _todayStr = () => new Date().toISOString().slice(0, 10);

const formatShortDate = (date) => {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const getDayPresets = () => {
    const presets = [];
    const today = new Date();

    for (let i = 1; i <= 7; i++) {
        const end = new Date(today);
        const start = new Date(today);
        start.setDate(today.getDate() - (i - 1));

        const startStr = start.toISOString().slice(0, 10);
        const endStr = end.toISOString().slice(0, 10);

        const label = i === 1
            ? `1 Day (${formatShortDate(start)})`
            : `${i} Days (${formatShortDate(start)} - ${formatShortDate(end)})`;

        presets.push({ days: i, label, startDate: startStr, endDate: endStr });
    }
    return presets;
};

const DAY_PRESETS = getDayPresets();

const MetricItem = ({ metric, selected, onToggle, isDark }) => {
    if (selected) {
        return (
            <div
                onClick={() => onToggle(metric.key)}
                className="relative overflow-hidden rounded-lg p-3 shadow-sm cursor-pointer border-2 transition-all hover:shadow-md"
                style={{
                    backgroundColor: isDark ? metric.darkBg : metric.lightBg,
                    borderColor: metric.color,
                }}
            >
                <div
                    className="absolute left-0 top-0 bottom-0 w-1"
                    style={{ backgroundColor: metric.color }}
                />
                <div className="flex items-center justify-between ml-2">
                    <div className="flex items-center gap-3">
                        <div
                            className="w-8 h-8 rounded flex items-center justify-center text-white"
                            style={{ backgroundColor: metric.color }}
                        >
                            {React.cloneElement(metric.icon, { sx: { fontSize: 18 } })}
                        </div>
                        <span className="text-sm font-bold" style={{ color: metric.color }}>
                            {metric.label}
                        </span>
                    </div>
                    <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: metric.color }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={() => onToggle(metric.key)}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center gap-3 hover:shadow-md transition-shadow cursor-pointer"
        >
            <div className="w-8 h-8 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400">
                {React.cloneElement(metric.icon, { sx: { fontSize: 18 } })}
            </div>
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{metric.label}</span>
        </div>
    );
};

const ResultCard = ({ metricKey, count, isDark }) => {
    const metric = METRICS.find((m) => m.key === metricKey);
    if (!metric) return null;

    return (
        <Fade in timeout={400}>
            <div
                className="relative overflow-hidden rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border"
                style={{
                    backgroundColor: isDark ? metric.darkBg : metric.lightBg,
                    borderColor: isDark ? `${metric.color}40` : `${metric.color}30`,
                }}
            >
                <div
                    className="absolute left-0 top-0 bottom-0 w-1"
                    style={{ backgroundColor: metric.color }}
                />
                <div className="flex justify-between items-start mb-4 ml-2">
                    <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm"
                        style={{ backgroundColor: metric.color }}
                    >
                        {React.cloneElement(metric.icon, { sx: { fontSize: 22 } })}
                    </div>
                    <TrendingUp sx={{ color: metric.color, opacity: 0.4, fontSize: 22 }} />
                </div>
                <div className="ml-2">
                    <div className="text-4xl font-bold mb-1" style={{ color: metric.color }}>
                        {count?.toLocaleString() ?? 0}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{metric.label}</div>
                </div>
            </div>
        </Fade>
    );
};

const MyDayScreen = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const breadcrumbs = [{ title: "My Day" }];

    const [selectedMetrics, setSelectedMetrics] = React.useState([]);
    const [selectedDays, setSelectedDays] = React.useState(1);
    const [isManualMode, setIsManualMode] = React.useState(false);
    const [manualStartDate, setManualStartDate] = React.useState(_todayStr());
    const [manualEndDate, setManualEndDate] = React.useState(_todayStr());
    const [dateError, setDateError] = React.useState("");
    const [results, setResults] = React.useState(null);

    const [fetchMyDay, { isLoading, isFetching, error }] = useLazyGetMyDaySummaryQuery();
    useAppMeta("My Day");

    const activePreset = useMemo(() => {
        return DAY_PRESETS.find((p) => p.days === selectedDays) || DAY_PRESETS[0];
    }, [selectedDays]);

    const toggleMetric = (key) => {
        setSelectedMetrics((prev) =>
            prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key],
        );
    };

    const selectAllMetrics = () => setSelectedMetrics(METRICS.map((m) => m.key));

    const clearAllMetrics = () => {
        setSelectedMetrics([]);
        setSelectedDays(1);
        setManualStartDate(_todayStr());
        setManualEndDate(_todayStr());
        setDateError("");
        setResults(null);
        setIsManualMode(false);
    };

    const handleSearch = async () => {
        if (!selectedMetrics.length) return;
        setDateError("");

        let params;
        if (isManualMode) {
            const _start = new Date(manualStartDate);
            const _end = new Date(manualEndDate);
            if (_start > _end) {
                setDateError("Start date cannot be after end date");
                return;
            }
            const _rangeDays = Math.floor((_end - _start) / (1000 * 60 * 60 * 24)) + 1;
            if (_rangeDays > MAX_RANGE_DAYS) {
                setDateError(`Date range cannot exceed ${MAX_RANGE_DAYS} days`);
                return;
            }
            params = { metrics: selectedMetrics, startDate: manualStartDate, endDate: manualEndDate };
        } else {
            params = { metrics: selectedMetrics, startDate: activePreset.startDate, endDate: activePreset.endDate };
        }

        try {
            const res = await fetchMyDay(params).unwrap();
            setResults(res);
        } catch (err) {
            // error handled by RTK Query
        }
    };

    const totalCount = useMemo(() => {
        if (!results?.data) return 0;
        return Object.values(results.data).reduce((sum, val) => sum + (Number(val) || 0), 0);
    }, [results]);

    const isBusy = isLoading || isFetching;

    const dateDisplay = isManualMode
        ? `${new Date(manualStartDate).toLocaleDateString()} - ${new Date(manualEndDate).toLocaleDateString()}`
        : activePreset.label.replace(/^\d+\sDays?\s\(/, "").replace(/\)$/, "");

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} isBusy={isBusy}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <Box className="p-4 md:p-6">
                    <Grid container spacing={4}>
                        {/* LEFT — Metrics Sidebar */}
                        <Grid item xs={12} lg={3}>
                            <Box className="sticky top-4 space-y-4">
                                {/* Metrics Header */}
                                <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                                    <div className="flex items-center gap-2">
                                        <TrendingUp sx={{ fontSize: 20, color: isDark ? "#fff" : "#000" }} />
                                        <Typography variant="subtitle1" fontWeight={700} className="dark:text-white">
                                            Metrics
                                        </Typography>
                                    </div>
                                    <span className="bg-black dark:bg-white text-white dark:text-black text-xs font-bold px-2 py-0.5 rounded-full">
                                        {selectedMetrics.length}
                                    </span>
                                </div>

                                {/* All / Clear */}
                                <div className="grid grid-cols-2 gap-2">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={selectAllMetrics}
                                        disabled={selectedMetrics.length === METRICS.length}
                                        className="normal-case text-xs py-1.5"
                                        sx={{
                                            borderColor: isDark ? "#fff" : "#000",
                                            color: isDark ? "#fff" : "#000",
                                            fontSize: "0.75rem",
                                            textTransform: "none",
                                        }}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={clearAllMetrics}
                                        disabled={!selectedMetrics.length && !results}
                                        color="error"
                                        className="normal-case text-xs py-1.5"
                                        sx={{ fontSize: "0.75rem", textTransform: "none" }}
                                    >
                                        Clear
                                    </Button>
                                </div>

                                {/* Metrics List */}
                                <div className="space-y-2">
                                    {METRICS.map((metric) => (
                                        <MetricItem
                                            key={metric.key}
                                            metric={metric}
                                            selected={selectedMetrics.includes(metric.key)}
                                            onToggle={toggleMetric}
                                            isDark={isDark}
                                        />
                                    ))}
                                </div>
                            </Box>
                        </Grid>

                        {/* RIGHT — Date Range & Results */}
                        <Grid item xs={12} lg={9}>
                            <Box className="space-y-6">
                                {/* Controls Row */}
                                <Box className="flex items-end gap-3 flex-wrap">
                                    {!isManualMode ? (
                                        <>
                                            <FormControl size="small" className="min-w-[280px]">
                                                <Select
                                                    value={selectedDays}
                                                    onChange={(e) => setSelectedDays(e.target.value)}
                                                    displayEmpty
                                                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-white"
                                                    sx={{
                                                        "& .MuiOutlinedInput-notchedOutline": {
                                                            border: "1px solid",
                                                            borderColor: isDark ? "#374151" : "#e5e7eb",
                                                        },
                                                        borderRadius: "0.5rem",
                                                    }}
                                                    startAdornment={
                                                        <CalendarMonth sx={{ fontSize: 18, color: "text.secondary", mr: 1 }} />
                                                    }
                                                >
                                                    {DAY_PRESETS.map((preset) => (
                                                        <MenuItem key={preset.days} value={preset.days} sx={{ fontSize: "0.875rem" }}>
                                                            {preset.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<Edit sx={{ fontSize: 18 }} />}
                                                onClick={() => setIsManualMode(true)}
                                                sx={{
                                                    textTransform: "none",
                                                    fontSize: "0.8rem",
                                                    py: 1,
                                                    borderColor: isDark ? "#fff" : "#000",
                                                    color: isDark ? "#fff" : "#000",
                                                }}
                                            >
                                                Add Manually
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Box className="flex items-end gap-2 flex-wrap">
                                                <TextField
                                                    size="small"
                                                    type="date"
                                                    label="Start Date"
                                                    InputLabelProps={{ shrink: true }}
                                                    value={manualStartDate}
                                                    onChange={(e) => { setManualStartDate(e.target.value); setDateError(""); }}
                                                    sx={{
                                                        width: 160,
                                                        "& .MuiOutlinedInput-root": {
                                                            bgcolor: isDark ? "#1f2937" : "#fff",
                                                        },
                                                    }}
                                                    className="dark:text-white"
                                                />
                                                <TextField
                                                    size="small"
                                                    type="date"
                                                    label="End Date"
                                                    InputLabelProps={{ shrink: true }}
                                                    value={manualEndDate}
                                                    onChange={(e) => { setManualEndDate(e.target.value); setDateError(""); }}
                                                    sx={{
                                                        width: 160,
                                                        "& .MuiOutlinedInput-root": {
                                                            bgcolor: isDark ? "#1f2937" : "#fff",
                                                        },
                                                    }}
                                                    className="dark:text-white"
                                                />
                                            </Box>

                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<CalendarMonth sx={{ fontSize: 18 }} />}
                                                onClick={() => { setIsManualMode(false); setDateError(""); }}
                                                sx={{ textTransform: "none", fontSize: "0.8rem", py: 1 }}
                                            >
                                                Use Presets
                                            </Button>
                                        </>
                                    )}

                                    <Button
                                        variant="contained"
                                        size="medium"
                                        disabled={!selectedMetrics.length || isBusy}
                                        onClick={handleSearch}
                                        startIcon={isBusy ? <Refresh className="animate-spin" /> : <TrendingUp />}
                                        sx={{
                                            bgcolor: isDark ? "#fff" : "#000",
                                            color: isDark ? "#000" : "#fff",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: "0.85rem",
                                            px: 3,
                                            py: 1,
                                            "&:hover": { bgcolor: isDark ? "#e5e5e5" : "#333" },
                                        }}
                                    >
                                        {isBusy ? "Loading..." : "Generate"}
                                    </Button>
                                </Box>

                                {!!dateError && (
                                    <Typography color="error" variant="caption" className="block">
                                        {dateError}
                                    </Typography>
                                )}

                                <Divider sx={{ borderColor: isDark ? "#374151" : "#e5e7eb" }} />

                                {/* Results Header */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Typography variant="h5" fontWeight={700} className="dark:text-white">
                                            Results
                                        </Typography>
                                        {!!results?.data && (
                                            <span className="bg-green-700 dark:bg-green-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                                                Total: {totalCount.toLocaleString()}
                                            </span>
                                        )}
                                    </div>
                                    <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
                                        {dateDisplay}
                                    </Typography>
                                </div>

                                {/* Loading Skeletons */}
                                {isBusy && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {[1, 2, 3].map((i) => (
                                            <Skeleton
                                                key={i}
                                                variant="rounded"
                                                height={160}
                                                className="rounded-xl"
                                                sx={{ bgcolor: isDark ? "#374151" : "#e5e7eb" }}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Results Grid */}
                                {!!results?.data && !isBusy && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {Object.entries(results.data).map(([key, count]) => (
                                            <ResultCard key={key} metricKey={key} count={count} isDark={isDark} />
                                        ))}
                                    </div>
                                )}

                                {/* Empty State */}
                                {!results?.data && !isBusy && !error && (
                                    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl p-8 flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-300 dark:border-gray-600 min-h-[300px]">
                                        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                                            <Insights sx={{ fontSize: 32, color: isDark ? "#9ca3af" : "#9ca3af" }} />
                                        </div>
                                        <Typography variant="h6" fontWeight={700} gutterBottom className="dark:text-white">
                                            No Data for Selection
                                        </Typography>
                                        <Typography variant="body2" className="max-w-sm text-gray-500 dark:text-gray-400">
                                            Adjust your date range or select different metrics from the sidebar to view detailed results.
                                        </Typography>
                                    </div>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default MyDayScreen;